import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { AvatarConfig } from "@shared/types";
import type { Path } from "./Path";
import { buildRunnerCharacter } from "./runnerCharacter";

/**
 * Multiplayer opponent renderer using the same rounded bean language as the
 * playable character. It stays lightweight but no longer falls back to the old
 * blocky avatar model.
 */
export class Ghost {
  group = new THREE.Group();
  /** Latest authoritative position from the network. */
  targetS = 0;
  targetLaneOffset = 0;
  /** Server-supplied velocity (m/s) — used for extrapolation between ticks. */
  targetVelocityS = 0;
  targetVelocityL = 0;
  /** Smoothed values used for rendering. */
  s = 0;
  laneOffset = 0;
  /** Last network tick timestamp (performance.now()), drives extrapolation horizon. */
  lastTickAt = 0;
  /** Constant per-slot perpendicular offset — keeps players visually distinct. */
  readonly slotOffset: number;

  private nameSprite?: THREE.Sprite;
  private path: Path;
  private body = new THREE.Group();
  private leftLeg = new THREE.Group();
  private rightLeg = new THREE.Group();
  private runT = 0;
  /** Network-reported player state. Drives the airborne jump animation. */
  private state: "run" | "jump" | "hit" | "finished" | "falling" = "run";
  /** Smoothed jump arc height — climbs while state === "jump", decays after. */
  private jumpY = 0;
  /** Y offset imposed by Game (e.g. ghost is currently riding a platform). */
  surfaceY = 0;
  /** Local fall simulation triggered when network state goes to "falling". */
  private falling = false;
  private fallStartedAt = 0;
  private fallPos = new THREE.Vector3();
  private fallVel = new THREE.Vector3();
  private fallSpin = new THREE.Vector3();

  constructor(
    public id: string,
    displayName: string,
    avatar: AvatarConfig,
    slotIdx: number,
    path: Path,
  ) {
    this.path = path;
    this.slotOffset = computeSlotOffset(slotIdx);

    // Shared runner geometry — identical to the local player + every preview
    // screen. Wrap the returned `group` in our own body so we can scale it
    // down for the multiplayer view without affecting other consumers.
    const parts = buildRunnerCharacter(avatar);
    this.body = parts.group;
    // Match the local player's PLAYER_SCALE in Player.ts exactly so
    // ghosts read at the same physical size on every screen — and so
    // the head/feet offsets line up for the "land on head" logic and
    // the jump-arc height the peer reports.
    this.body.scale.setScalar(0.25);
    this.leftLeg = parts.leftLeg;
    this.rightLeg = parts.rightLeg;
    this.group.add(this.body);

    // Floating name tag — sits above the runner's head. Updated each
    // frame so it stays attached above the (possibly jumping) body.
    this.nameSprite = makeNameSprite(displayName);
    this.nameSprite.position.set(0, 0.95, 0);
    this.group.add(this.nameSprite);

    // Debug overlay — toggled via `window.WR_DEBUG_MP = true` in the
    // browser console. Renders a small canvas-text sprite under the
    // runner with live network + state info.
    if (typeof window !== "undefined" && (window as { WR_DEBUG_MP?: boolean }).WR_DEBUG_MP) {
      this.debugSprite = makeNameSprite("debug");
      this.debugSprite.position.set(0, 0.55, 0);
      this.debugSprite.scale.set(1.4, 0.4, 1);
      this.group.add(this.debugSprite);
    }
  }

  private debugSprite?: THREE.Sprite;

  /** Authoritative vertical offset from the peer's last tick. The
   *  ghost lerps `jumpY` toward this so jump arcs mirror the local
   *  player exactly, instead of guessing with a fixed target. */
  private targetY = 0;
  /** Peer's reported vertical velocity. Used to extrapolate Y between
   *  ticks (gravity-style integration) so the arc stays smooth at 20Hz. */
  private targetVy = 0;
  /** Last reported grounded flag — drives run vs jump-tuck animation. */
  private targetGrounded = true;

  setTarget(
    s: number,
    laneOffset: number,
    velocityS = 0,
    velocityL = 0,
    y?: number,
    vy?: number,
    grounded?: boolean,
  ) {
    this.targetS = s;
    this.targetLaneOffset = laneOffset;
    this.targetVelocityS = velocityS;
    this.targetVelocityL = velocityL;
    if (y != null) this.targetY = y;
    if (vy != null) this.targetVy = vy;
    if (grounded != null) this.targetGrounded = grounded;
    this.lastTickAt = performance.now();
  }

  private lastJumpSeq = 0;
  /** Local replay of a fresh jump arc (called on every jumpSeq increment
   *  from the peer). Resets jumpY downward so the climb-toward-target
   *  lerp visibly re-pops the runner. */
  pulseJump(seq: number | undefined) {
    if (seq == null) return;
    if (seq === this.lastJumpSeq) return;
    this.lastJumpSeq = seq;
    // Drop the arc back to ~0 so the upcoming jumpTarget lerp visibly
    // pops the runner again. Without this an air-jump while state is
    // still "jump" would be invisible — jumpY already at peak.
    this.jumpY = 0;
  }

  setState(state: "run" | "jump" | "hit" | "finished" | "falling") {
    if (state === "falling" && !this.falling) {
      // Kick off a local fall simulation from wherever the ghost currently is.
      // The peer will broadcast "run" again on respawn — we end the fall there.
      this.falling = true;
      this.fallStartedAt = performance.now();
      this.fallPos.copy(this.group.position);
      // Small initial outward + downward kick + tumble spin. We don't know the
      // exact velocity from the peer, but this reads as "they fell off."
      this.fallVel.set((Math.random() - 0.5) * 2.4, 0.3, (Math.random() - 0.5) * 2.4);
      this.fallSpin.set(
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 6,
      );
    }
    if (state !== "falling" && this.falling) {
      this.falling = false;
      // Reset rotation so the run cycle takes over cleanly.
      this.group.rotation.set(0, 0, 0);
    }
    this.state = state;
  }

  /** Current rendered forward velocity — used for player↔ghost collision impulse. */
  getVelocityS(): number { return this.targetVelocityS; }
  getVelocityL(): number { return this.targetVelocityL; }

  update(dt: number) {
    // Falling: run a local gravity sim from where the ghost was at fall-onset.
    // Ignore path positioning for the duration. The peer will send "run"
    // again on respawn — that flip clears `this.falling` and the normal path
    // logic takes over.
    if (this.falling) {
      this.fallVel.y -= 9.8 * dt;
      this.fallPos.addScaledVector(this.fallVel, dt);
      this.group.position.copy(this.fallPos);
      this.group.rotation.x += this.fallSpin.x * dt;
      this.group.rotation.y += this.fallSpin.y * dt;
      this.group.rotation.z += this.fallSpin.z * dt;
      this.fallSpin.multiplyScalar(Math.exp(-0.18 * dt));
      // After 2.6s, hide the ghost so they don't fall forever — the respawn
      // tick should arrive around 3s, at which point setState clears falling.
      if (performance.now() - this.fallStartedAt > 2600) {
        this.group.visible = false;
      }
      return;
    }
    // Make sure we're visible when not falling (in case we hid above).
    if (!this.group.visible) this.group.visible = true;
    // Extrapolation: predict where the ghost actually is *right now* using the
    // last reported velocity and elapsed time since that tick. Then smoothly
    // lerp toward that predicted point so the ghost glides instead of
    // snapping every 50ms. Cap extrapolation at 200ms so a packet-stalled
    // ghost doesn't shoot off forever.
    const now = performance.now();
    const sincePeer = Math.min(0.2, (now - this.lastTickAt) / 1000);
    const predictedS = this.targetS + this.targetVelocityS * sincePeer;
    const predictedLane = this.targetLaneOffset + this.targetVelocityL * sincePeer;

    const prevS = this.s;
    const prevLane = this.laneOffset;
    // Time-normalized lerp — identical feel at 30/60/120 Hz.
    const k = 1 - Math.exp(-22 * dt);
    this.s += (predictedS - this.s) * k;
    this.laneOffset += (predictedLane - this.laneOffset) * k;
    const pos = this.path.positionAt(this.s, this.laneOffset + this.slotOffset);
    // Maze-rush: the path's heightAt is flat (0.18). Each lane has its
    // own curved Y profile via laneWarp(secIdx, laneIdx, t). Pick the
    // lane whose lateral centre is closest to the ghost's lateral
    // position at this s, and apply that lane's dy so the ghost rises +
    // dips on the same ribbon as the local player. Otherwise remote
    // players appear "skating" laterally while the lanes go up and down
    // around them.
    if (this.path.mazeRush) {
      const secIdx = this.path.mazeRushSectionIdx(this.s);
      if (secIdx >= 0) {
        const mr = this.path.mazeRush;
        let bestIdx = 0;
        let bestDist = Infinity;
        const lateral = this.laneOffset + this.slotOffset;
        for (let i = 0; i < mr.laneCount; i++) {
          const w = this.path.mazeRushLaneWarpAt(i, this.s);
          const d = Math.abs(lateral - w.dx);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        const warp = this.path.mazeRushLaneWarpAt(bestIdx, this.s);
        pos.y += warp.dy;
      }
    }
    // Authoritative Y sync — the peer broadcasts their actual y +
    // vy each tick, so we extrapolate using gravity-style integration
    // between packets, then smoothly lerp to the next reported value.
    // This makes every other player's jump arc + landing match the
    // height they themselves see on their device, not a fixed guess.
    const sincePeerS = Math.min(0.2, (now - this.lastTickAt) / 1000);
    const predictedY = Math.max(0, this.targetY + this.targetVy * sincePeerS - 0.5 * 9.8 * sincePeerS * sincePeerS);
    // Critical-damp jumpY toward the predicted Y at ~14Hz — snappy
    // enough that you see the second of a double-jump but smooth
    // enough that 20Hz packets don't strobe.
    const yLerp = 1 - Math.exp(-14 * dt);
    this.jumpY += (predictedY - this.jumpY) * yLerp;
    this.group.position.set(pos.x, pos.y + this.surfaceY, pos.z);
    this.group.rotation.y = this.path.yawAt(this.s);

    // Debug overlay (toggle: `window.WR_DEBUG_MP = true` in console).
    if (this.debugSprite) {
      const mat = this.debugSprite.material as THREE.SpriteMaterial;
      const old = mat.map;
      const canvas = document.createElement("canvas");
      canvas.width = 320; canvas.height = 96;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "rgba(15,23,42,0.85)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#a3e635";
      ctx.font = "bold 18px ui-monospace, monospace";
      ctx.textBaseline = "top";
      ctx.fillText(`s=${this.s.toFixed(1)}  l=${(this.laneOffset).toFixed(2)}`, 8, 6);
      ctx.fillText(`y=${this.jumpY.toFixed(2)}  vy=${this.targetVy.toFixed(2)}`, 8, 28);
      ctx.fillText(`${this.state}  ground=${this.targetGrounded}`, 8, 50);
      ctx.fillText(`jseq=${this.lastJumpSeq}`, 8, 72);
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      mat.map = tex;
      mat.needsUpdate = true;
      old?.dispose?.();
    }

    const move = Math.min(1, Math.hypot(this.s - prevS, this.laneOffset - prevLane) / Math.max(0.0001, dt) / 9);
    this.runT += dt * (4 + move * 9);
    const sway = Math.sin(this.runT);
    const breath = Math.sin(performance.now() * 0.003) * 0.018;
    // Animation pose: prefer the peer's grounded flag (authoritative)
    // and fall back to local heuristics if the field isn't present
    // yet (older clients during a rolling deploy).
    const airborne =
      this.targetGrounded === false ||
      this.state === "jump" ||
      this.jumpY > 0.05;
    if (airborne) {
      // Jump pose: legs tucked, arms-out feel via slight body lean back.
      this.body.position.y = this.jumpY + breath * 0.3;
      this.body.rotation.x = -0.18;
      this.body.rotation.z = 0;
      // Legs pull up into the body.
      this.leftLeg.rotation.x = -0.85;
      this.rightLeg.rotation.x = -0.85;
      this.leftLeg.position.y = 0.78;
      this.rightLeg.position.y = 0.78;
    } else {
      // Run cycle (existing).
      this.body.rotation.z = sway * 0.13 * move;
      this.body.rotation.x = Math.abs(sway) * 0.04 * move;
      this.body.position.y = Math.abs(Math.sin(this.runT * 2)) * 0.025 * move + breath;
      this.leftLeg.rotation.x = -sway * 0.4 * move;
      this.rightLeg.rotation.x = sway * 0.4 * move;
      this.leftLeg.position.y = 0.58 + Math.max(0, sway) * 0.14 * move;
      this.rightLeg.position.y = 0.58 + Math.max(0, -sway) * 0.14 * move;
    }
  }

  dispose() {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        const m = o.material;
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
        else m.dispose();
      } else if (o instanceof THREE.Sprite) {
        o.material.map?.dispose();
        o.material.dispose();
      }
    });
  }
}

/**
 * Lateral offset for slot `idx` (0..MAX_PLAYERS-1).
 * Slot 0 sits at center; odd indices go right, even-non-zero go left.
 * Spacing 1.1 m keeps 8 players within the ±5 m path width with bumping room.
 */
export function computeSlotOffset(idx: number): number {
  if (idx <= 0) return 0;
  const pair = Math.ceil(idx / 2);
  const sign = idx % 2 === 1 ? 1 : -1;
  return sign * pair * 1.1;
}

function capsulePart(radius: number, length: number, color: THREE.ColorRepresentation) {
  const mesh = new THREE.Mesh(
    new THREE.CapsuleGeometry(radius, length, 12, 20),
    new THREE.MeshStandardMaterial({ color, roughness: 0.58, metalness: 0.03, transparent: true, opacity: 0.92 }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function addHat(body: THREE.Group, hatId: string) {
  if (hatId === "none") return;
  switch (hatId) {
    case "cap-blue":
    case "cap-red": {
      const color = hatId === "cap-blue" ? 0x2563eb : 0xdc2626;
      const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.36 });
      const crown = new THREE.Mesh(new SoftBox(0.78, 0.32, 0.78), mat);
      crown.position.y = 2.92;
      crown.castShadow = true;
      body.add(crown);
      const brim = new THREE.Mesh(new SoftBox(0.78, 0.08, 0.45), mat);
      brim.position.set(0, 2.78, 0.5);
      brim.castShadow = true;
      body.add(brim);
      break;
    }
    case "beanie": {
      const mat = new THREE.MeshStandardMaterial({ color: 0xef476f, roughness: 0.36 });
      const crown = new THREE.Mesh(new SoftBox(0.82, 0.4, 0.82), mat);
      crown.position.y = 2.96;
      crown.castShadow = true;
      body.add(crown);
      const pom = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      );
      pom.position.y = 3.22;
      body.add(pom);
      break;
    }
    case "crown": {
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd166, metalness: 0.6, roughness: 0.3 });
      const band = new THREE.Mesh(new SoftBox(0.78, 0.18, 0.78), goldMat);
      band.position.y = 2.85;
      band.castShadow = true;
      body.add(band);
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (Math.abs(i) + Math.abs(j) !== 1) continue;
          const spike = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 4), goldMat);
          spike.position.set(i * 0.3, 3.05, j * 0.3);
          body.add(spike);
        }
      }
      break;
    }
    case "party-hat": {
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.42, 0.95, 16),
        new THREE.MeshStandardMaterial({ color: 0xa855f7, roughness: 0.45 }),
      );
      cone.position.y = 3.18;
      cone.castShadow = true;
      body.add(cone);
      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0xffd166 }),
      );
      tip.position.y = 3.7;
      body.add(tip);
      break;
    }
    case "halo": {
      const halo = new THREE.Mesh(
        new THREE.TorusGeometry(0.42, 0.08, 12, 28),
        new THREE.MeshStandardMaterial({
          color: 0xfef08a,
          emissive: 0xfff3a8,
          emissiveIntensity: 0.45,
          metalness: 0.3,
          roughness: 0.25,
        }),
      );
      halo.rotation.x = Math.PI / 2;
      halo.position.y = 3.18;
      body.add(halo);
      break;
    }
  }
}

/** Procedural shirt material — bakes the chosen pattern into a CanvasTexture
 *  to match the in-game Player exactly. Solid pattern is a flat color. */
function makeShirtMaterial(base: THREE.Color, pattern: string): THREE.MeshStandardMaterial {
  if (pattern === "solid") {
    return new THREE.MeshStandardMaterial({ color: base, roughness: 0.55, metalness: 0.03 });
  }
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#" + base.getHexString();
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  if (pattern === "stripes") {
    const stripeH = size / 8;
    for (let i = 0; i < 8; i += 2) ctx.fillRect(0, i * stripeH, size, stripeH);
  } else if (pattern === "dots") {
    for (let y = 24; y < size; y += 48) {
      for (let x = 24; x < size; x += 48) {
        ctx.beginPath();
        ctx.arc(x + (Math.floor(y / 48) % 2 === 0 ? 0 : 24), y, 10, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (pattern === "star") {
    ctx.fillStyle = "#ffd166";
    const drawStar = (cx: number, cy: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const rad = i % 2 === 0 ? r : r * 0.45;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 40; y < size; y += 80) {
      for (let x = 40; x < size; x += 80) {
        drawStar(x + (Math.floor(y / 80) % 2 === 0 ? 0 : 40), y, 22);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.55, metalness: 0.03 });
}

function makeNameSprite(text: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "rgba(15, 23, 42, 0.72)";
  roundRect(ctx, 8, 8, 240, 48, 18);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.75)";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 28px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text.slice(0, 14), 128, 32);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(1.7, 0.42, 1);
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
